'use client';

import { useEffect, useState, useMemo, useRef } from 'react';
import ReactGridLayout, { WidthProvider } from 'react-grid-layout';
import { cn } from '@workspace/ui/lib/utils';
import {
  SocialTile,
  ImageTile,
  VideoTile,
  NoteTile,
  EmptyTileWrapper,
  DeleteButton,
} from '../index';
import {
  BASE_TILES,
  COLS_DESKTOP,
  COLS_MOBILE,
} from '@/src/constants/constants';
import { IProfile, ITile } from 'types/src/types';
import { Card } from '@workspace/ui/components/card';
import { useProfileUIStore } from '../../store/index';
import { Icon, useToast } from '@/src/components/ui';
import { ResizeControls } from './ResizeControls';
import { TitleTile } from './tiles/TitleTile';
import { IUITile } from '@/src/types/ITile';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import { useResponsiveTileHeight } from '@/src/hooks/use-responsive-tile-height';
import { useTilesStore } from '../../store/tiles-store';
import { deleteTile } from '@/src/actions/tiles';
import { useBoolean } from '@/src/hooks/use-boolean';
import { useCreateTileRequest } from '@/src/hooks/use-create-tile-request';
import { CustomTile } from './tiles/CustomTile';
import { updateTileOrderClient } from '@/src/actions/updateTileOrder.client';
import { Button } from '@workspace/ui/components/button';

const WidthProviderGridLayout = WidthProvider(ReactGridLayout);

type Props = {
  profile: IProfile;
  isMyProfile: boolean;
  initialTiles: ITile[] | null;
};

function debounce<F extends (...args: any[]) => void>(
  func: F,
  abortController: AbortController | null,
  wait: number,
) {
  if (abortController) {
    abortController.abort();
  }

  let timeout: NodeJS.Timeout | null = null;
  return (...args: Parameters<F>) => {
    if (timeout) clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
}

export const GridLayout = ({ profile, isMyProfile, initialTiles }: Props) => {
  const toast = useToast();
  const isDesktop = useBoolean(true);
  const isDragging = useBoolean(false);
  const isUpdating = useBoolean(false);
  const [hoverDisabledStates, setHoverDisabledStates] = useState<
    Record<string, boolean>
  >({});
  const storeTiles = useTilesStore((s) => s.tiles);
  const tiles: IUITile[] = Array.isArray(storeTiles) ? storeTiles : [];

  const setTiles = useTilesStore((state) => state.setTiles);
  const removeTile = useTilesStore((state) => state.removeTile);
  const requestedType = useCreateTileRequest((s) => s.requestedType);
  const clearRequest = useCreateTileRequest((s) => s.clearRequest);
  const [touchedTileId, setTouchedTileId] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const lastSavedTilesRef = useRef<IUITile[]>([]);

  const saveTilesDebounced = debounce(
    async () => {
      const currentTiles = useTilesStore.getState().tiles;
      if (abortRef.current && !abortRef.current.signal.aborted) {
        abortRef.current.abort();
        abortRef.current = null;
      }

      const controller = new AbortController();
      abortRef.current = controller;

      let hasChanges = false;

      for (const tile of currentTiles) {
        const original = lastSavedTilesRef.current.find((t) => t.i === tile.i);
        if (!original) continue;

        if (
          tile.x !== original.x ||
          tile.y !== original.y ||
          tile.w !== original.w ||
          tile.h !== original.h ||
          tile.mw !== original.mw ||
          tile.mh !== original.mh ||
          tile.mx !== original.mx ||
          tile.my !== original.my
        ) {
          hasChanges = true;
          break;
        }
      }

      if (!hasChanges || !isMyProfile) {
        return;
      }

      try {
        toast.updating();
        const result = await updateTileOrderClient(
          currentTiles,
          false,
          controller.signal,
        );
        lastSavedTilesRef.current = result;

        useTilesStore.setState({ tiles: result });
        toast.updated();
      } catch (error) {
        if (error instanceof DOMException && error.name === 'AbortError') {
          return;
        }
        console.error('Error saving tiles:', error);
        toast.error('Failed to save changes');
      } finally {
        abortRef.current = null;
      }
    },
    abortRef.current,
    2500,
  );

  const previewMode = useProfileUIStore((state) => state.previewMode);
  const isMobileView = useProfileUIStore((state) => state.isMobileView);
  const responsiveTileHeight = useResponsiveTileHeight(isMobileView);
  const isMobile = !isDesktop.value || isMobileView;

  const hasEmptyTiles = useMemo(() => {
    return tiles.some(
      (tile) => tile.static && (!tile.content || tile.content.trim() === ''),
    );
  }, [tiles]);

  const [shakeIds, setShakeIds] = useState<Set<string>>(new Set());

  const triggerShakeEmptyTiles = (opts?: { scroll?: boolean }) => {
    const scroll = opts?.scroll ?? true;

    const empties = tiles.filter(
      (t) => t.static && (!t.content || t.content.trim() === ''),
    );
    if (empties.length === 0) return;

    const ids = empties.map((t) => t.i);
    setShakeIds(new Set(ids));

    if (scroll) {
      const el = document.querySelector(`[data-tile-id='${ids[0]}']`);
      el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    window.setTimeout(() => setShakeIds(new Set()), 450);
  };

  useEffect(() => {
    const checkScreenSize = () => {
      isDesktop.setValue(window.innerWidth >= 1280);
    };
    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  useEffect(() => {
    let tiles: IUITile[] = [];

    const tilesData = initialTiles?.map((tile) => ({
      ...tile,
    })) as IUITile[];
    tiles = tilesData?.length ? tilesData : BASE_TILES;

    const migrated = tiles.map((tile) => ({
      ...tile,
      mx: tile.mx ?? 0,
      my: tile.my ?? 0,
      mw: tile.mw ?? 1,
      mh: tile.mh ?? 3,

      x: tile.x ?? 0,
      y: tile.y ?? 0,
      w: tile.w ?? 1,
      h: tile.h ?? 3,
      isEditing: tile.isEditing ?? false,
    }));
    setTiles(migrated);
    lastSavedTilesRef.current = migrated;
  }, [initialTiles, setTiles]);

  const occupiedPositionsDesktop = useMemo(() => {
    const occupied = new Set<string>();
    tiles.forEach((tile) => {
      const x = tile.x;
      const y = tile.y;
      const w = tile.w;
      const h = tile.h;

      for (let xx = x; xx < x + w; xx++) {
        for (let yy = y; yy < y + h; yy++) {
          occupied.add(`${xx},${yy}`);
        }
      }
    });
    return occupied;
  }, [tiles]);

  const occupiedPositionsMobile = useMemo(() => {
    const occupied = new Set<string>();
    tiles.forEach((tile) => {
      const x = tile.mx;
      const y = tile.my;
      const w = tile.mw;
      const h = tile.mh;

      for (let xx = x; xx < x + w; xx++) {
        for (let yy = y; yy < y + h; yy++) {
          occupied.add(`${xx},${yy}`);
        }
      }
    });
    return occupied;
  }, [tiles]);

  const findFreePosition = (
    width: number,
    height: number,
    isDesktopParam: boolean,
  ) => {
    const columns = isDesktopParam ? COLS_DESKTOP : COLS_MOBILE;
    const occupiedPositions = isDesktopParam
      ? occupiedPositionsDesktop
      : occupiedPositionsMobile;

    const maxY = Math.max(
      ...tiles.map((tile) =>
        isDesktopParam ? tile.y + tile.h : tile.my + tile.mh,
      ),
      0,
    );
    const maxRows = Math.ceil(maxY + height);
    for (let y = 0; y < maxRows; y++) {
      for (let x = 0; x <= columns - width; x++) {
        let canPlace = true;
        for (let dx = 0; dx < width && canPlace; dx++) {
          for (let dy = 0; dy < height && canPlace; dy++) {
            if (occupiedPositions.has(`${x + dx},${Math.ceil(y + dy)}`)) {
              canPlace = false;
            }
          }
        }
        if (canPlace) {
          return { x, y };
        }
      }
    }

    return { x: 0, y: maxY };
  };

  const addNewTile = (type: ITile['type']) => {
    if (hasEmptyTiles) {
      toast.tip('Fill or delete empty tiles first before adding a new one');
      triggerShakeEmptyTiles({ scroll: true });
      return;
    }

    if (abortRef.current && !abortRef.current.signal.aborted) {
      abortRef.current.abort();
      abortRef.current = null;
    }

    const randomId = Math.random().toString(36).substr(2, 9);
    const timestamp = Date.now();
    const counter = tiles.length;
    const newId = `${type}-${timestamp}-${randomId}-${counter}`;

    let newTileData: Partial<IUITile>;

    if (type === 'add-title') {
      const posD = findFreePosition(4, 1, true);
      const posM = findFreePosition(2, 1, false);
      newTileData = {
        w: 4,
        h: 1,
        x: posD.x,
        y: posD.y,

        mw: 2,
        mh: 1,
        mx: posM.x,
        my: posM.y,

        minW: 2,
        minH: 1,
        maxW: 4,
        maxH: 1,
        type: 'add-title',
        title: 'Title',
        content: '',
        static: true,
      };
    } else {
      const posD = findFreePosition(1, 3, true);
      const posM = findFreePosition(1, 3, false);
      newTileData = {
        w: 1,
        h: 3,
        x: posD.x,
        y: posD.y,

        mw: 1,
        mh: 3,
        mx: posM.x,
        my: posM.y,

        minW: 1,
        minH: 3,
        maxW: 2,
        maxH: 6,
        type,
        title:
          type === 'add-custom'
            ? ''
            : type.replace('add-', 'Add').replace('-', ' ') +
              (type === 'add-note' ? '...' : ''),
        static: true,
      };
    }
    const newTile: IUITile = { i: newId, ...newTileData } as IUITile;
    setTiles([...tiles, newTile]);
    lastSavedTilesRef.current = [...lastSavedTilesRef.current, newTile];
  };

  useEffect(() => {
    if (requestedType) {
      addNewTile(requestedType);
    }

    return () => {
      clearRequest();
    };
  }, [requestedType, addNewTile]);

  const handleHoverStateChange =
    (tileId: string) => (shouldDisable: boolean) => {
      setHoverDisabledStates((prev) => ({
        ...prev,
        [tileId]: shouldDisable,
      }));
    };

  const renderTileContent = (tile: IUITile, touchedTileId: string | null) => {
    switch (tile.type) {
      case 'add-note':
        return (
          <NoteTile
            onHoverStateChange={handleHoverStateChange(tile.i)}
            isMyProfile={isMyProfile}
            tile={tile}
            touchedTileId={touchedTileId}
            setTouchedTileId={setTouchedTileId}
          />
        );
      case 'add-social':
        return (
          <SocialTile
            onHoverStateChange={handleHoverStateChange(tile.i)}
            isMyProfile={isMyProfile}
            profileAvatar={profile.avatarUrl}
            profileName={profile.fullName as string}
            tile={tile}
            touchedTileId={touchedTileId}
            setTouchedTileId={setTouchedTileId}
          />
        );
      case 'add-image':
        return (
          <ImageTile
            onHoverStateChange={handleHoverStateChange(tile.i)}
            isMyProfile={isMyProfile}
            tile={tile}
          />
        );
      case 'add-video':
        return (
          <VideoTile
            onHoverStateChange={handleHoverStateChange(tile.i)}
            isMyProfile={isMyProfile}
            tile={tile}
            isMobileScreen={isMobile}
            touchedTileId={touchedTileId}
            setTouchedTileId={setTouchedTileId}
          />
        );
      case 'add-title':
        return (
          <TitleTile
            onHoverStateChange={handleHoverStateChange(tile.i)}
            isMyProfile={isMyProfile}
            tile={tile}
            touchedTileId={touchedTileId}
            setTouchedTileId={setTouchedTileId}
          />
        );
      case 'add-custom':
        return (
          <CustomTile
            onHoverStateChange={handleHoverStateChange(tile.i)}
            isMyProfile={isMyProfile}
            tile={tile}
            touchedTileId={touchedTileId}
            setTouchedTileId={setTouchedTileId}
            isMobileView={isMobile}
          />
        );
      default:
        return <p className="">{tile.title}</p>;
    }
  };

  const handleLayoutChange = (layout: IUITile[]) => {
    useTilesStore.setState((state) => ({
      tiles: state.tiles.map((tile) => {
        const updated = layout.find((l) => l.i === tile.i);
        if (!updated) return tile;
        return isMobile
          ? {
              ...tile,
              mx: updated.x,
              my: updated.y,
              mw: updated.w,
              mh: updated.h,
            }
          : { ...tile, x: updated.x, y: updated.y, w: updated.w, h: updated.h };
      }),
    }));

    saveTilesDebounced();
  };

  const layout = useMemo(() => {
    const safeTiles = useTilesStore.getState();
    return safeTiles.tiles
      .filter((tile) =>
        isMobile
          ? typeof tile.mx === 'number' &&
            typeof tile.my === 'number' &&
            typeof tile.mw === 'number' &&
            typeof tile.mh === 'number'
          : typeof tile.x === 'number' &&
            typeof tile.y === 'number' &&
            typeof tile.w === 'number' &&
            typeof tile.h === 'number',
      )
      .map(({ i, x, y, w, h, mx, my, mw, mh, isEditing }) => {
        const base = isMobile
          ? { i, x: mx, y: my, w: mw, h: mh }
          : { i, x, y, w, h };
        const result = {
          ...base,
          static: !!isEditing,
        };
        return result;
      });
  }, [tiles, isMobile]);

  const handleDragStart = () => {
    if (hasEmptyTiles) {
      toast.tip('Fill all new tiles first before moving them');
      triggerShakeEmptyTiles({ scroll: true });
      return;
    }
    isDragging.onTrue();
    document.body.style.userSelect = 'none';
    document.body.style.webkitUserSelect = 'none';
  };

  const handleDragStop = () => {
    isDragging.onFalse();
    document.body.style.userSelect = '';
    document.body.style.webkitUserSelect = '';
  };

  const handleRemoveTileById = async (tileIndex: string, tileId?: number) => {
    if (!tileId) {
      removeTile(tileIndex);
      return;
    } else {
      try {
        await deleteTile(tileId);
        removeTile(tileIndex);
        lastSavedTilesRef.current = lastSavedTilesRef.current.filter(
          (t) => t.i !== tileIndex,
        );
      } catch (error) {
        toast.error('Failed to delete tile');
      }
    }

    clearRequest();
  };

  const handleResizeWithAnimation = (
    uiId: string,
    newW: number,
    newH: number,
  ) => {
    if (hasEmptyTiles) {
      toast.tip('Fill or delete all empty tiles first before resizing');
      triggerShakeEmptyTiles({ scroll: true });
      return;
    }
    const element = document.querySelector(`[data-tile-id='${uiId}']`);
    if (element) element.classList.add('resizing');

    useTilesStore.setState((state) => ({
      tiles: state.tiles.map((t) => {
        return t.i === uiId
          ? isMobile
            ? { ...t, mw: newW, mh: newH * 3 }
            : { ...t, w: newW, h: newH * 3 }
          : t;
      }),
    }));

    setTimeout(() => {
      if (element) element.classList.remove('resizing');
    }, 300);

    saveTilesDebounced();
  };

  const handleTouchEnd = (tileId: string) => {
    setTouchedTileId(tileId);
  };

  const handleContainerClick = (
    event: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>,
  ) => {
    let target: EventTarget | null = null;
    if ('target' in event) {
      target = event.target;
    }
    if (target && target instanceof HTMLElement) {
      const isTileClicked = target.closest('[data-tile-id]');
      if (!isTileClicked && touchedTileId !== null) {
        setTouchedTileId(null);
      }
    }
  };

  return (
    <div
      className={cn('w-[320px] md:w-[500px] xl:w-full m-auto', {})}
      onClick={handleContainerClick}
      onTouchEnd={handleContainerClick}
    >
      <WidthProviderGridLayout
        className="layout"
        layout={layout}
        cols={isMobile ? COLS_MOBILE : COLS_DESKTOP}
        margin={isMobile ? [8, 8] : [20, 20]}
        rowHeight={responsiveTileHeight}
        resizeHandles={['se', 'ne']}
        isResizable={false}
        draggableHandle={isMobile ? '.grip-handle' : undefined}
        isDraggable={isMyProfile && !previewMode && !hasEmptyTiles}
        compactType="vertical"
        preventCollision={false}
        onLayoutChange={handleLayoutChange}
        onDragStart={handleDragStart}
        onDragStop={handleDragStop}
        useCSSTransforms={true}
      >
        {tiles.map((tile) => (
          <div
            key={tile.i}
            data-grid={
              isMobile
                ? {
                    x: tile.mx,
                    y: tile.my,
                    w: tile.mw,
                    h: tile.mh,
                    static: !!tile.isEditing,
                  }
                : {
                    x: tile.x,
                    y: tile.y,
                    w: tile.w,
                    h: tile.h,
                    static: !!tile.isEditing,
                  }
            }
            data-tile-id={tile.i}
            style={{
              userSelect: isDragging.value ? 'none' : 'auto',
              WebkitUserSelect: isDragging.value ? 'none' : 'auto',
            }}
            className={cn({ 'w-full': tile.type === 'add-title' })}
          >
            {tile.static || !tile.content ? (
              !previewMode &&
              isMyProfile && (
                <EmptyTileWrapper
                  isMyProfile={isMyProfile}
                  isAddContentButton={false}
                  disableHover={hoverDisabledStates[tile.i]}
                  className={cn(shakeIds.has(tile.i) && 'tile-shake')}
                >
                  {isMyProfile && !previewMode && (
                    <DeleteButton
                      className="group-hover flex cursor-pointer"
                      onClick={() => handleRemoveTileById(tile.i)}
                    />
                  )}
                  {renderTileContent(tile, touchedTileId)}
                </EmptyTileWrapper>
              )
            ) : (
              <Card
                key={tile.id}
                data-tile-id={tile.i}
                className={cn(
                  'rounded-2xl border p-0 relative group h-full bg-white shadow-none',
                  {
                    'cursor-grabbing': !previewMode && isMyProfile,
                    'bg-red-youtube': tile.type === 'add-video',
                    'xl:w-1/2 w-full': tile.type === 'add-title' && !isMobile,
                    'bg-transparent': tile.type === 'add-image',
                    'cursor-default': !isMyProfile || tile.isEditing,
                    'select-none': isDragging.value,
                  },
                )}
                style={{
                  userSelect: isDragging.value ? 'none' : 'auto',
                  WebkitUserSelect: isDragging.value ? 'none' : 'auto',
                }}
                onTouchEnd={() => handleTouchEnd(tile.i)}
              >
                {isMyProfile && !previewMode && tile.type === 'add-title' && (
                  <DeleteButton
                    isTitle
                    className="group-hover:flex cursor-pointer"
                    onClick={() =>
                      handleRemoveTileById(tile.i, tile.id as number)
                    }
                  />
                )}
                {renderTileContent(tile, touchedTileId)}
                {isMyProfile &&
                  !previewMode &&
                  !tile.isEditing &&
                  !isUpdating.value &&
                  (!isMobile || tile.i === touchedTileId) && (
                    <>
                      {tile.type !== 'add-title' && (
                        <ResizeControls
                          currentW={isMobile ? tile.mw : tile.w}
                          currentH={isMobile ? tile.mh : tile.h}
                          onResize={(newW, newH) => {
                            handleResizeWithAnimation(tile.i, newW, newH);
                          }}
                          isTitleHint={
                            tile.iconName === 'steam' ||
                            tile.iconName === 'discord'
                          }
                          isMobileScreen={isMobile}
                          onDelete={() =>
                            handleRemoveTileById(tile.i, tile.id as number)
                          }
                        />
                      )}
                      {isMobile && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="absolute translate-x-1/2 -bottom-2 right-1/2 z-10 p-1 rounded-full bg-black/80 size-8 grip-handle"
                        >
                          <Icon name="grip" className="size-5 text-white" />
                        </Button>
                      )}
                    </>
                  )}
              </Card>
            )}
          </div>
        ))}
      </WidthProviderGridLayout>
    </div>
  );
};
